import {
  Box,
  Button,
  Card,
  DestructiveModal,
  Divider,
  Flex,
  Heading,
  Input,
  ListItem,
  Text,
  Toast,
  useOpenModal,
} from '@modusign/design-system';
import { useDelegateWorkspace } from '@modusign/openapi-client-user';
import { ReactNode, useState } from 'react';
import { useHistory } from 'react-router';

import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentUserName } from '@app/hooks/workspace';
import { logger } from '@app/libs/logger';

import { MemberSelectModal } from '../../member';
import { GrantWorkspaceRoleModal } from '../../workspaceMemberInvitation';

const AGREEMENT_TEXT = '동의합니다';

export default function ChangeWorkspaceOwnerCard() {
  const workspaceId = useWorkspaceId();
  const history = useHistory();
  const openModal = useOpenModal();

  const currentUserName = useCurrentUserName();

  const [member, setMember] = useState<null | { id: string; name: string; userEmail: string }>(
    null
  );
  const [role, setRole] = useState<null | { id: string; name: string }>(null);

  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

  const [agreementInput, setAgreementInput] = useState('');

  const { mutate, isLoading } = useDelegateWorkspace({
    mutation: {
      onSuccess: () => {
        Toast.success({ description: '최고 관리자를 변경했습니다.' });
        setConfirmModalOpen(false);
        history.goBack();
      },
      onError: () => {
        Toast.error({ description: '최고 관리자 변경에 실패했습니다.' });
      },
    },
  });

  return (
    <Card>
      <Heading>최고 관리자 변경</Heading>
      <Text as="div" color="textNeutralLight" mt={6}>
        공용 워크스페이스에서 최고 관리자 역할은 1명만 가질 수 있습니다.
      </Text>
      <Box mt={6} py={7}>
        <Box borderTop="1px solid" borderTopColor="tableBorderNeutralLight">
          <Row activated={!!member} index={1}>
            <Flex alignItems="center" flex="auto" justifyContent="space-between" sx={{ gap: 6 }}>
              {member ? (
                <Text fontSize="sm" fontWeight="medium">
                  {member.name}
                  <Text color="textNeutralVeryLight" fontSize="sm" ml="2px">
                    ({member.userEmail})
                  </Text>
                </Text>
              ) : (
                <Text color="textNeutralLight" fontSize="sm" fontWeight="medium">
                  변경할 멤버를 선택해 주세요.
                  <Text color="textNegativeBase" fontWeight="bold">
                    *
                  </Text>
                </Text>
              )}
              <Button
                fontSize="13px"
                height="30px"
                px={5}
                py={2}
                variant="subtle"
                whiteSpace="pre"
                onClick={() => {
                  openModal(({ onClose }) => (
                    <MemberSelectModal
                      title="최고 관리자 변경"
                      onClose={onClose}
                      onSelect={(member) => {
                        setMember(member);
                        onClose();
                      }}
                    />
                  ));
                }}
              >{`멤버 ${member ? '변경' : '선택'}`}</Button>
            </Flex>
          </Row>
          <Row activated={!!role} index={2}>
            <Flex alignItems="center" flex="auto" justifyContent="space-between" sx={{ gap: 6 }}>
              {role ? (
                <Text fontSize="sm" fontWeight="medium">
                  {role.name}
                  <Text color="textNeutralVeryLight" fontSize="sm" ml="2px">
                    (내 역할)
                  </Text>
                </Text>
              ) : (
                <Text color="textNeutralLight" fontSize="sm" fontWeight="medium">
                  최고 관리자 변경 후 내 역할을 선택해 주세요.
                  <Text color="textNeutralVeryLight" fontSize="sm">
                    (선택)
                  </Text>
                </Text>
              )}
              <Button
                fontSize="13px"
                height="30px"
                ml="2px"
                px={5}
                py={2}
                variant="subtle"
                whiteSpace="pre"
                onClick={() => {
                  openModal(({ onClose, isOpen }) => (
                    <GrantWorkspaceRoleModal
                      isOpen={isOpen}
                      title="변경할 역할 선택"
                      onClose={onClose}
                      onConfirm={(role) => {
                        setRole(role);
                        onClose();
                      }}
                    />
                  ));
                }}
              >{`역할 ${role ? '변경' : '선택'}`}</Button>
            </Flex>
          </Row>
        </Box>
      </Box>
      <Flex gap={3} justifyContent="center" mt={6} pb={2} pt={4}>
        <Button onClick={() => history.goBack()}>취소</Button>
        <Button disabled={!member} variant="primary" onClick={() => setConfirmModalOpen(true)}>
          변경
        </Button>
      </Flex>
      <DestructiveModal
        closeButtonLabel="취소"
        confirmButtonLabel="확인"
        disabled={agreementInput !== AGREEMENT_TEXT}
        isLoading={isLoading}
        isOpen={isConfirmModalOpen}
        maxWidth="sm"
        title="최고 관리자 변경 안내"
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => {
          if (!member) {
            logger.error('Member is not selected while trying to delegate workspace.');
            return;
          }
          mutate({ workspaceId, data: { memberId: member.id, roleId: role?.id } });
        }}
      >
        <Flex
          as="ul"
          backgroundColor="boxBackgroundNegativeBase"
          border="1px solid"
          borderColor="boxBorderNegativeBase"
          flexDirection="column"
          gap={2}
          lineHeight="20px"
          mb={6}
          px={6}
          py={7}
        >
          <ListItem
            color="textNegativeBase"
            sx={{
              fontWeight: 'medium',
            }}
          >
            최고 관리자는 멤버가 서명을 요청했거나 요청받은 모든 문서를 열람할 수 있습니다.
          </ListItem>
          <ListItem>
            {currentUserName}님은 서명 요청했거나 공유받은 문서가 아닌 문서를 열람할 수 없습니다.
          </ListItem>
          <ListItem>
            문서, 템플릿 등을 {currentUserName}님의 개인 워크스페이스로 이전할 수 없습니다.
          </ListItem>
          <ListItem>
            최고 관리자가 {currentUserName}님을 워크스페이스에서 삭제할 수 있습니다.
          </ListItem>
        </Flex>
        <Box
          backgroundColor="boxBackgroundNeutralBase"
          border="1px solid"
          borderColor="boxBorderNeutralBase"
          color="textNeutralLight"
        >
          <Box px={6} py={4}>
            최고 관리자 변경에 따른 권한 이전에 동의할 경우
            <Text color="textNeutralBase"> ‘{AGREEMENT_TEXT}’</Text>를 입력해 주세요.
          </Box>
          <Divider />
          <Flex alignItems="center" color="textNeutralBase" gap={3} p={6}>
            <Box>최고 관리자 변경에 따른 권한 이전에</Box>
            <Input
              maxWidth="200px"
              placeholder={AGREEMENT_TEXT}
              value={agreementInput}
              onChange={(e) => setAgreementInput(e.target.value)}
            />
          </Flex>
        </Box>
      </DestructiveModal>
    </Card>
  );
}

function Row({
  index,
  activated,
  children,
}: {
  index: number;
  activated: boolean;
  children: ReactNode;
}) {
  return (
    <Flex
      alignItems="center"
      borderBottom="1px solid"
      borderBottomColor="tableBorderNeutralLight"
      px={5}
      py={7}
    >
      <Flex
        alignItems="center"
        bg={activated ? 'statusBackgroundPrimaryBase' : '#f0f0f0'}
        borderRadius="50%"
        color="textNeutralDark"
        fontSize="10px"
        fontWeight="medium"
        height="21px"
        justifyContent="center"
        mr={1}
        width="21px"
      >
        {index}
      </Flex>
      {children}
    </Flex>
  );
}
